import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { usePageLoading } from '../contexts/PageLoadingContext';

/**
 * Dados do consumo diário
 */
interface DailyUsage {
  date: string;
  meter_type: string;
  usage: number;
}

/**
 * Statement (período mensal)
 */
interface Statement {
  period: string; // formato: "2024-01-01" ou "Jan-2024"
  reading?: number;
  consumption: number;
}

/**
 * Dados da API principal
 */
interface ApiDataResponse {
  customer_id: string;
  meter_types: string[];
  daily_usage: DailyUsage[];
  last_statements: Statement[];
}

/**
 * Dados detalhados de um mês específico da API de statements
 */
interface MonthDetailResponse {
  begin_of_period: string;
  end_of_period: string;
  meter: string;
  goal: {
    base: string;
    value: number;
  };
  average_consumption: number;
  total_consumption: number;
  total_cost: string;
  history: DailyUsage[];
}

/**
 * Dados processados de um mês
 */
export interface MonthDetail {
  beginOfPeriod: string;
  endOfPeriod: string;
  meter: string;
  goal: {
    base: string;
    value: number;
    status: 'Abaixo' | 'Dentro' | 'Ultrapassou';
  };
  averageConsumption: string;
  totalConsumption: number;
  totalCost: string;
  days: number[];
  coldWaterConsumption: number[];
  hotWaterConsumption: number[];
  mineralWaterConsumption: number[];
}

/**
 * Dados processados para WaterReportPage
 */
export interface WaterReportData {
  customerId: string;
  meterTypes: string[];
  
  // Dados diários do período atual
  dailyDays: number[];
  highestUsage: number;
  totalColdWater: number;
  totalHotWater: number;
  coldWaterConsumption: number[];
  hotWaterConsumption: number[];
  
  // Last statements (meses anteriores) para accordion
  lastStatements: Array<{
    period: string;       // formato original para API
    periodDate: Date;     // Date object para formatação
    month: string;        // nome do mês formatado
    monthId: string;      // ID para accordion (month-Janeiro, etc)
    reading?: number;
    consumption: number;
  }>;
  
  // Monthly consumption para gráfico de barras
  monthlyLabels: string[];
  monthlyConsumption: number[];
}

interface UseWaterReportResult {
  data: WaterReportData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  
  // Função para buscar detalhes de um mês específico
  loadMonthDetails: (period: string, monthId: string) => Promise<MonthDetail | null>;
  monthDetails: Record<string, MonthDetail>;
  loadingMonths: Record<string, boolean>;
}

/**
 * Formata data para DD/MM/YYYY
 */
function formatDateBR(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Subtrai um dia da data
 */
function subtractOneDay(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
}

/**
 * Formata período para o formato da API (Jan-2024)
 */
function formatPeriodForApi(periodDate: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[periodDate.getMonth()]}-${periodDate.getFullYear()}`;
}

/**
 * Hook para buscar dados do relatório de água.
 * 
 * @param residenceId - ID da residência (RGI/customer_id)
 */
export function useWaterReport(residenceId: string): UseWaterReportResult {
  const [data, setData] = useState<WaterReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthDetails, setMonthDetails] = useState<Record<string, MonthDetail>>({});
  const [loadingMonths, setLoadingMonths] = useState<Record<string, boolean>>({});
  const { startPageLoading, stopPageLoading } = usePageLoading();

  // Esconde o preloader após o React processar isLoading = false
  useEffect(() => {
    if (!isLoading) {
      stopPageLoading();
    }
  }, [isLoading, stopPageLoading]);

  /**
   * Busca detalhes de um mês específico
   */
  const loadMonthDetails = useCallback(async (period: string, monthId: string): Promise<MonthDetail | null> => {
    // Se já existe, retorna sem fazer nova requisição
    if (monthDetails[monthId]) {
      return monthDetails[monthId];
    }

    // Se já está carregando, retorna null
    if (loadingMonths[monthId]) {
      return null;
    }

    setLoadingMonths(prev => ({ ...prev, [monthId]: true }));

    try {
      const url = `/api/v1/statements/${period}/details/${residenceId}/water`;
      const response = await api.get<MonthDetailResponse>(url);
      const apiData = response.data;

      const detail: MonthDetail = {
        beginOfPeriod: formatDateBR(apiData.begin_of_period),
        endOfPeriod: formatDateBR(subtractOneDay(apiData.end_of_period)),
        meter: apiData.meter,
        goal: {
          base: apiData.goal?.base || 'average',
          value: apiData.goal?.value || 0,
          status: apiData.goal?.value > apiData.average_consumption ? 'Ultrapassou' : 'Abaixo',
        },
        averageConsumption: parseFloat(String(apiData.average_consumption)).toFixed(3),
        totalConsumption: apiData.total_consumption,
        totalCost: apiData.total_cost,
        days: [...new Set(apiData.history.map(h => new Date(h.date + 'T00:00:00').getDate()))],
        coldWaterConsumption: apiData.history.filter(h => h.meter_type === 'AGUA-FRIA').map(h => h.usage),
        hotWaterConsumption: apiData.history.filter(h => h.meter_type === 'AGUA-QUENTE').map(h => h.usage),
        mineralWaterConsumption: apiData.history.filter(h => h.meter_type === 'AGUA-MINERAL').map(h => h.usage),
      };

      setMonthDetails(prev => ({ ...prev, [monthId]: detail }));
      setLoadingMonths(prev => ({ ...prev, [monthId]: false }));
      
      return detail;
    } catch (err) {
      console.error('Error loading month details:', err);
      setLoadingMonths(prev => ({ ...prev, [monthId]: false }));
      return null;
    }
  }, [residenceId, monthDetails, loadingMonths]);

  const fetchData = useCallback(async () => {
    if (!residenceId) {
      setError('ID da residência não fornecido');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    startPageLoading();
    setError(null);

    try {
      // Usa a mesma rota api_data que a WaterHomePage
      const response = await api.get<ApiDataResponse>(
        `/consumo/agua/${residenceId}/api_data`
      );

      const apiData = response.data;
      console.log('[useWaterReport] API Response:', apiData);

      // Filtra dados diários por tipo de medidor
      const coldWater = apiData.daily_usage.filter(d => d.meter_type === 'AGUA-FRIA');
      const hotWater = apiData.daily_usage.filter(d => d.meter_type === 'AGUA-QUENTE');
      
      // Extrai dias (apenas água fria para evitar duplicatas)
      const dailyDays = coldWater.map(d => new Date(d.date + 'T00:00:00').getDate());
      
      // Consumo diário por tipo
      const coldWaterConsumption = coldWater.map(d => d.usage);
      const hotWaterConsumption = hotWater.map(d => d.usage);
      
      // Totais
      const totalColdWater = coldWaterConsumption.reduce((sum, v) => sum + v, 0);
      const totalHotWater = hotWaterConsumption.reduce((sum, v) => sum + v, 0);
      
      // Maior consumo para escala do gráfico
      const allUsages = [...coldWaterConsumption, ...hotWaterConsumption];
      const maxUsage = Math.max(...allUsages, 0);
      const highestUsage = Math.ceil(maxUsage / 100) * 100;

      // Processa statements para accordion
      const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const shortMonths = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      
      const lastStatements = apiData.last_statements.map(stmt => {
        const periodDate = new Date(stmt.period + 'T00:00:00');
        const monthName = months[periodDate.getMonth()];
        const monthId = `month-${monthName}`;
        const period = formatPeriodForApi(periodDate);
        
        return {
          period,
          periodDate,
          month: monthName,
          monthId,
          reading: stmt.reading,
          consumption: stmt.consumption,
        };
      });

      // Dados para gráfico mensal (reversed para ordem cronológica)
      const monthlyLabels = lastStatements.map(s => shortMonths[s.periodDate.getMonth()]).reverse();
      const monthlyConsumption = lastStatements.map(s => s.consumption).reverse();

      const processedData: WaterReportData = {
        customerId: apiData.customer_id,
        meterTypes: apiData.meter_types,
        dailyDays,
        highestUsage,
        totalColdWater,
        totalHotWater,
        coldWaterConsumption,
        hotWaterConsumption,
        lastStatements,
        monthlyLabels,
        monthlyConsumption,
      };

      console.log('[useWaterReport] Processed Data:', processedData);
      setData(processedData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar dados do relatório';
      setError(errorMessage);
      console.error('useWaterReport error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [residenceId, startPageLoading]);

  // Busca dados quando o residenceId muda
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    loadMonthDetails,
    monthDetails,
    loadingMonths,
  };
}
